/**
 * Desktop History Page — T22
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Clock, Calendar, Briefcase, Loader2, ChevronDown } from 'lucide-react';
import config from 'config';

const API = config.API_URL;

export default function DesktopHistoryPage() {
  const { token } = useOutletContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');

  const getDateRange = useCallback(() => {
    const now = new Date();
    let start, end;
    if (period === 'week') {
      const d = new Date(now);
      const day = d.getDay() || 7;
      d.setDate(d.getDate() - day + 1);
      start = d.toISOString().slice(0, 10);
      const e = new Date(d);
      e.setDate(e.getDate() + 6);
      end = e.toISOString().slice(0, 10);
    } else if (period === 'month') {
      start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
      end = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
    } else {
      start = `${now.getFullYear()}-01-01`;
      end = `${now.getFullYear()}-12-31`;
    }
    return { start, end };
  }, [period]);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    const { start, end } = getDateRange();
    try {
      const res = await fetch(`${API}/api/employees/me/history?start_date=${start}&end_date=${end}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setData(await res.json());
    } catch {}
    setLoading(false);
  }, [token, getDateRange]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-[#C9A24D] animate-spin" /></div>;

  const s = data?.summary || {};

  return (
    <div className="p-8 max-w-5xl" data-testid="desktop-history-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-white">Historique heures</h1>
          <p className="text-white/40 text-sm mt-1">Heures travaillées, missions réalisées, jours travaillés</p>
        </div>
        <div className="relative">
          <select value={period} onChange={e => setPeriod(e.target.value)} className="bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm appearance-none pr-10" data-testid="desktop-history-period">
            <option value="week">Cette semaine</option>
            <option value="month">Ce mois</option>
            <option value="year">Cette année</option>
          </select>
          <ChevronDown className="absolute right-3 top-3 w-4 h-4 text-white/30 pointer-events-none" />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-5 mb-8" data-testid="desktop-history-stats">
        <div className="p-5 rounded-2xl bg-white/[0.03] border border-white/5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-white/30 text-xs font-medium uppercase tracking-wider">Heures travaillées</span>
            <Clock className="w-4 h-4 text-[#C9A24D]" />
          </div>
          <p className="text-3xl font-bold text-white">{s.total_hours || 0}<span className="text-base text-white/30 ml-1">h</span></p>
        </div>
        <div className="p-5 rounded-2xl bg-white/[0.03] border border-white/5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-white/30 text-xs font-medium uppercase tracking-wider">Missions</span>
            <Briefcase className="w-4 h-4 text-blue-400" />
          </div>
          <p className="text-3xl font-bold text-white">{s.mission_count || 0}</p>
        </div>
        <div className="p-5 rounded-2xl bg-white/[0.03] border border-white/5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-white/30 text-xs font-medium uppercase tracking-wider">Jours travaillés</span>
            <Calendar className="w-4 h-4 text-emerald-400" />
          </div>
          <p className="text-3xl font-bold text-white">{s.days_worked || 0}</p>
        </div>
        <div className="p-5 rounded-2xl bg-white/[0.03] border border-white/5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-white/30 text-xs font-medium uppercase tracking-wider">Shifts</span>
            <Clock className="w-4 h-4 text-purple-400" />
          </div>
          <p className="text-3xl font-bold text-white">{s.shift_count || 0}</p>
        </div>
      </div>

      {/* Table */}
      {(data?.entries || []).length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 rounded-2xl bg-white/[0.02] border border-white/5">
          <Clock className="w-14 h-14 text-white/[0.06] mb-4" />
          <p className="text-white/20 text-sm">Aucun historique pour cette période</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-white/5 overflow-hidden" data-testid="desktop-history-table">
          <table className="w-full">
            <thead>
              <tr className="bg-white/[0.02]">
                <th className="text-left text-white/30 text-xs font-medium uppercase tracking-wider px-5 py-3">Date</th>
                <th className="text-left text-white/30 text-xs font-medium uppercase tracking-wider px-5 py-3">Horaires</th>
                <th className="text-left text-white/30 text-xs font-medium uppercase tracking-wider px-5 py-3">Notes</th>
                <th className="text-right text-white/30 text-xs font-medium uppercase tracking-wider px-5 py-3">Heures</th>
              </tr>
            </thead>
            <tbody>
              {data.entries.map(e => (
                <tr key={e.id} className="border-t border-white/5 hover:bg-white/[0.02] transition" data-testid={`desktop-history-row-${e.id}`}>
                  <td className="px-5 py-4 text-white text-sm font-medium">
                    {new Date(e.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </td>
                  <td className="px-5 py-4">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-blue-500/10 text-blue-300 text-xs font-medium">
                      <Clock className="w-3.5 h-3.5" />
                      {e.shift_start} — {e.shift_end}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-white/40 text-sm">{e.notes || '-'}</td>
                  <td className="px-5 py-4 text-right text-[#C9A24D] text-sm font-semibold">{e.total_hours}h</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-white/10 bg-white/[0.02]">
                <td colSpan={3} className="px-5 py-3 text-white/40 text-sm font-medium">Total</td>
                <td className="px-5 py-3 text-right text-[#C9A24D] text-sm font-bold">{s.total_hours || 0}h</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
